import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './screens/Login';
import SuperAdminDashboard from './screens/SuperAdminDashboard';
import AdminDashboard from './screens/AdminDashboard';
import ReporterDashboard from './screens/ReporterDashboard';

function App() {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Session validation function
  const validateSession = async (sessionId, dashAdminId) => {
    try {
      console.log('🔍 Validating session with server...');
      
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 1,
          isActiveFilter: true,
          isActive: true,
          isSearchFilter: false,
          search: ""
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.status === true) {
        console.log('✅ Session validation successful');
        return true;
      } else {
        console.log('❌ Session validation failed:', data.message);
        return false;
      }
    } catch (error) {
      console.error('💥 Session validation error:', error);
      // If validation fails due to network error, assume session is valid
      return true;
    }
  };

  // Restore session from localStorage on app load
  useEffect(() => {
    const restoreSession = async () => {
      try {
        console.log('🔄 Attempting to restore session from localStorage...');
        
        const storedUser = localStorage.getItem('loggedInUser');
        const sessionId = localStorage.getItem('sessionId');
        const dashAdminId = localStorage.getItem('dashAdminId');
        
        if (storedUser && sessionId && dashAdminId) {
          console.log('📦 Found stored session data');
          
          const user = JSON.parse(storedUser);
          console.log('👤 Restored user:', user);
          
          // Validate session with server
          const isValid = await validateSession(sessionId, dashAdminId);
          
          if (isValid) {
            console.log('✅ Session restored successfully');
            setLoggedInUser(user);
          } else {
            console.log('❌ Session invalid, clearing stored data');
            localStorage.removeItem('loggedInUser');
            localStorage.removeItem('sessionId');
            localStorage.removeItem('dashAdminId');
            localStorage.removeItem('loginResponse');
          }
        } else {
          console.log('📭 No stored session found');
        }
      } catch (error) {
        console.error('💥 Error restoring session:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('loggedInUser');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('dashAdminId');
        localStorage.removeItem('loginResponse');
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const handleLogout = () => {
    console.log('🚪 Logging out user');
    setLoggedInUser(null);
    // Clear all session data from localStorage
    localStorage.removeItem('loggedInUser');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('dashAdminId');
    localStorage.removeItem('loginResponse');
  };

  const handleLogin = (user) => {
    console.log('🔐 Login successful, user:', user);
    setLoggedInUser(user);
    // Store user data in localStorage for session persistence
    localStorage.setItem('loggedInUser', JSON.stringify(user));
  };

  // Show loading spinner while checking for existing session
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          loggedInUser ? (
            (() => {
              const redirectPath = loggedInUser.role === 'Super Admin' ? '/superadmin/dashboard' :
                loggedInUser.role === 'Admin' ? '/admin/dashboard' :
                loggedInUser.role === 'Approver' ? '/admin/dashboard' :
                loggedInUser.role === 'Reporter' ? '/reporter/dashboard' :
                '/superadmin/dashboard';
              console.log('🔄 Redirecting to:', redirectPath, 'for user role:', loggedInUser.role);
              return <Navigate to={redirectPath} replace />;
            })()
          ) : (
            <Login onLogin={handleLogin} />
          )
        } />
        <Route path="/login" element={
          loggedInUser ? (
            <Navigate to={
              loggedInUser.role === 'Super Admin' ? '/superadmin/dashboard' :
              loggedInUser.role === 'Admin' ? '/admin/dashboard' :
              loggedInUser.role === 'Approver' ? '/admin/dashboard' :
              loggedInUser.role === 'Reporter' ? '/reporter/dashboard' :
              '/superadmin/dashboard'
            } replace />
          ) : (
            <Login onLogin={handleLogin} />
          )
        } />
        <Route path="/superadmin/*" element={
          loggedInUser && loggedInUser.role === 'Super Admin' ? (
            (() => {
              console.log('✅ Accessing SuperAdmin dashboard for user:', loggedInUser);
              return <SuperAdminDashboard user={loggedInUser} onLogout={handleLogout} />;
            })()
          ) : (
            (() => {
              console.log('❌ Access denied to SuperAdmin. User:', loggedInUser);
              return <Navigate to="/login" replace />;
            })()
          )
        } />
        <Route path="/admin/*" element={
          loggedInUser && (loggedInUser.role === 'Admin' || loggedInUser.role === 'Approver') ? (
            <AdminDashboard user={loggedInUser} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" replace />
          )
        } />
        <Route path="/reporter/*" element={
          loggedInUser && loggedInUser.role === 'Reporter' ? (
            <ReporterDashboard user={loggedInUser} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" replace />
          )
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
